import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import type { SystemStyleObject, Theme } from '@mui/system';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type { HireStep, HireStepKey } from './hireR8trTypes';

type StepState = 'done' | 'current' | 'upcoming';

interface HireStepperProps {
  steps: HireStep[];
  activeKey: HireStepKey;
  completed: ReadonlySet<HireStepKey>;
  onSelect: (key: HireStepKey) => void;
}

const markerSx = (state: StepState): SystemStyleObject<Theme> => {
  switch (state) {
    case 'done':
      return {
        bgcolor: 'text.primary',
        borderColor: 'text.primary',
        color: 'background.paper',
      };
    case 'current':
      return {
        bgcolor: 'primary.main',
        borderColor: 'primary.main',
        color: 'primary.contrastText',
      };
    case 'upcoming':
      return {
        bgcolor: 'transparent',
        borderColor: 'divider',
        color: 'text.secondary',
      };
    default: {
      const unreachable: never = state;
      return unreachable;
    }
  }
};

export const HireStepper = ({
  steps,
  activeKey,
  completed,
  onSelect,
}: HireStepperProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Box
      component="ol"
      sx={{ display: 'flex', alignItems: 'flex-start', m: 0, p: 0 }}
    >
      {steps.map((step, index) => {
        const previous = steps[index - 1];
        const reachedFromPrevious = !previous || completed.has(previous.key);
        const state: StepState =
          step.key === activeKey
            ? 'current'
            : completed.has(step.key)
              ? 'done'
              : 'upcoming';
        const reachable = reachedFromPrevious && state !== 'current';
        return (
          <Fragment key={step.key}>
            {index > 0 ? (
              <Box
                aria-hidden
                sx={{
                  flex: 1,
                  height: 2,
                  mt: '15px',
                  mx: 0.75,
                  borderRadius: 1,
                  bgcolor: reachedFromPrevious ? 'text.primary' : 'divider',
                  transition: 'background-color 0.25s ease',
                }}
              />
            ) : null}
            <Box component="li" sx={{ listStyle: 'none' }}>
              <Box
                component="button"
                type="button"
                disabled={!reachable}
                onClick={() => onSelect(step.key)}
                aria-current={state === 'current' ? 'step' : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                  p: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  font: 'inherit',
                  cursor: reachable ? 'pointer' : 'default',
                  '&:focus-visible .hire-step-marker': {
                    outline: 2,
                    outlineColor: 'text.primary',
                    outlineOffset: 2,
                  },
                  '&:hover:not(:disabled) .hire-step-label': {
                    color: 'text.primary',
                  },
                }}
              >
                <Box
                  className="hire-step-marker"
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: 2,
                    fontSize: 13,
                    fontWeight: 700,
                    transition:
                      'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease',
                    ...markerSx(state),
                  }}
                >
                  {state === 'done' ? (
                    <CheckIcon sx={{ fontSize: 16 }} />
                  ) : (
                    index + 1
                  )}
                </Box>
                <Box
                  component="span"
                  className="hire-step-label"
                  sx={{
                    display: {
                      xs: state === 'current' ? 'block' : 'none',
                      sm: 'block',
                    },
                    fontSize: 12,
                    fontWeight: state === 'current' ? 700 : 500,
                    color:
                      state === 'upcoming' ? 'text.secondary' : 'text.primary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(`hire.flow.steps.${step.copyKey}.label`)}
                </Box>
              </Box>
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
};
